"use client"

import { useState, useEffect, useRef, useContext, useCallback } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Button, Alert, Card, Icon, Spinner } from "../../../components"
import { CardSelect, CupomModal, CreditForm } from "../Forms"
import { CouponAppliedSection } from "../CouponUi"
import { normalizeCouponCode } from "../../../utils/couponBenefits"
import PaymentProcessing from "../PaymentProcessing"
import { Api } from "../../../api/Assinafy"
import { calendarDateFormat, formatBRL } from "../../../utils/Formats"
import "./styles.scss"
import Dashboard from "../../../layout/Dashboard"
import { ResponsiveAlert } from "../Alerts"
import { SessionContext } from "../../../routes/DashboardRoute"
import {
  buildPixPurchaseResumeState,
  isPixBilling,
  needsBillingProfileBeforePixPurchase,
  needsCreditCardForPlanChange,
  shouldRedirectToBillingProfile,
  willCreatePixInvoice as shouldCreatePixInvoice,
} from "../planPayment"
import PixPendingPayment from "../PixPendingPayment"
import { notifySubscriptionUpdated } from "../../../utils/subscriptionEvents"
import { getPaymentErrorMessage } from "../paymentErrors"

const ChangePlan = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [session] = useContext(SessionContext)
  const { user } = session || { user: {} }

  const selectedPlan = location.state?.selectedPlan || {}
  const currentPlan = location.state?.currentPlan || {}
  const returnHref = location.state?.fromPlans ? "/plans" : "/dashboard/workspace/plan"

  const planId = selectedPlan?.id
  const planName = selectedPlan?.name || "Plano"
  const planPrice = selectedPlan?.price || 0
  const planMaxDocuments = selectedPlan?.max_documents || selectedPlan?.max || 0
  const planBillingInterval = selectedPlan?.billing_interval || "Monthly"
  const planDisplayName = planBillingInterval === "Yearly" ? planName.replace(/\s+(anual|annual)$/i, "") : planName
  const isGoingToFree = selectedPlan?.name?.toLowerCase() === "free" || selectedPlan?.price === 0

  const [loadingCards, setLoadingCards] = useState(true)
  const [creditCards, setCreditCards] = useState([])
  const [paymentData, setPaymentData] = useState({
    isNewCard: true,
    selectedCardId: null,
    newCardData: {},
    isNewCardValid: false,
  })
  const [appliedCoupon, setAppliedCoupon] = useState(null)
  const [showCupomModal, setShowCupomModal] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [customerData, setCustomerData] = useState(null)
  const [needsCustomerUpdate, setNeedsCustomerUpdate] = useState(false)
  const [forceValidation, setForceValidation] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [planPreview, setPlanPreview] = useState(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [previewError, setPreviewError] = useState(null)
  const [pendingSubscription, setPendingSubscription] = useState(null)

  const isMountedRef = useRef(true)
  const timeoutRef = useRef(null)
  const submitInFlightRef = useRef(false)
  const resumePixPurchaseRef = useRef(false)
  const errorAlertRef = useRef(null)

  const previousPlan = currentPlan?.plan || currentPlan
  const previousPlanName = previousPlan?.name || "Plano Atual"

  const userName = user?.name || "Usuário"
  const accountId = session?.accounts?.[0]?.id

  const formatCalendarDate = (value) => calendarDateFormat(value, {
    day: "numeric",
    month: "long",
    year: "numeric",
  })

  const isDowngrade = planPreview?.direction === "Downgrade"
  const isPixPayment = isPixBilling(planPreview)
  const willCreatePixInvoice = shouldCreatePixInvoice(planPreview)
  const currentBillingInterval = currentPlan?.billing_interval || currentPlan?.plan?.billing_interval
  const needsCreditCard = needsCreditCardForPlanChange(
    planPreview,
    currentBillingInterval,
    planBillingInterval,
    !isGoingToFree
  )

  useEffect(() => {
    if (!errorMessage || !errorAlertRef.current) return

    errorAlertRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
    errorAlertRef.current.focus({ preventScroll: true })
  }, [errorMessage])

  const handleFormChange = useCallback((data) => {
    setPaymentData({
      isNewCard: true,
      selectedCardId: null,
      newCardData: data.formData,
      isNewCardValid: data.isValid,
    })
  }, [])

  const fetchPlanPreview = useCallback(async (targetPlanId, showLoading = true) => {
    if (!targetPlanId) return null

    if (showLoading) setLoadingPreview(true)
    setPreviewError(null)

    try {
      const response = await Api("subscription/upgradePreview")(targetPlanId)
      const preview = response?.data?.data
      if (response?.status !== 200 || !preview) {
        throw new Error(response?.data?.message || "Não foi possível calcular esta alteração de plano.")
      }

      if (isMountedRef.current) setPlanPreview(preview)
      return preview
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Não foi possível calcular esta alteração de plano."
      if (isMountedRef.current) {
        setPlanPreview(null)
        setPreviewError(message)
      }
      throw error
    } finally {
      if (isMountedRef.current && showLoading) setLoadingPreview(false)
    }
  }, [])

  useEffect(() => {
    if (!selectedPlan?.id) {
      navigate("/dashboard/workspace")
    }
  }, [selectedPlan?.id, navigate])

  useEffect(() => {
    if (!planId) return
    fetchPlanPreview(planId).catch(() => {})
  }, [planId, fetchPlanPreview])

  const fetchCustomerData = async () => {
    try {
      const response = await Api("customer/view")()

      if (!isMountedRef.current) return

      if (response?.status === 200 && response?.data?.data) {
        setCustomerData(response.data.data)

        const customer = response.data.data
        const requiredFields = ["name", "email", "taxpayer_id", "postal_code", "address_line1", "city", "state"]
        const missingFields = requiredFields.filter((field) => !customer[field])

        if (missingFields.length > 0) {
          setNeedsCustomerUpdate(true)
        }
      } else {
        setNeedsCustomerUpdate(true)
      }
    } catch (error) {
      console.error("Erro ao buscar dados do cliente:", error)
      if (isMountedRef.current) {
        setNeedsCustomerUpdate(true)
      }
    }
  }

  const getBillingProfileStatus = useCallback(async (preview) => {
    if (!accountId) {
      throw new Error("Não foi possível identificar a conta para verificar os dados de cobrança.")
    }

    const response = await Api("account/pendingActions")(accountId)
    if (response?.status !== 200 || !Array.isArray(response?.data?.data?.actions)) {
      throw new Error("Não foi possível verificar os dados de cobrança.")
    }

    return needsBillingProfileBeforePixPurchase(preview, response.data.data.actions)
      ? "missing"
      : "ready"
  }, [accountId])

  const updateCustomerData = async (cardData) => {
    try {
      const customerPayload = {
        name: cardData.holder_name,
        taxpayer_id: cardData.holder_taxpayer_id.replace(/\D/g, ""),
        phone_number: cardData.holder_phone.replace(/\D/g, ""),
        email: cardData.holder_email,
        address: {
          line1: cardData.address_line1,
          line2: cardData.address_line2 || "",
          house_number: cardData.house_number,
          neighborhood: cardData.neighborhood,
          city: cardData.city,
          state: cardData.state,
          postal_code: cardData.postal_code.replace(/\D/g, ""),
        },
      }

      const response = await Api("customer/update")(customerPayload)

      if (!isMountedRef.current) return false

      if (response?.status === 200) {
        setCustomerData(customerPayload)
        setNeedsCustomerUpdate(false)
        return true
      } else {
        throw new Error("Erro ao atualizar dados do cliente")
      }
    } catch (error) {
      console.error("Erro ao atualizar dados do cliente:", error)
      throw error
    }
  }

  const fetchCreditCards = async () => {
    try {
      setLoadingCards(true)
      const response = await Api("creditCard/index")()

      if (!isMountedRef.current) return

      if (response?.status === 200 && response?.data?.data) {
        setCreditCards(response.data.data)
        if (response.data.data.length > 0) {
          setPaymentData({
            isNewCard: false,
            selectedCardId: response.data.data[0].id,
            newCardData: {},
            isNewCardValid: false,
          })
        }
      } else {
        setCreditCards([])
      }
    } catch (error) {
      console.error("Erro ao buscar cartões:", error)
      if (isMountedRef.current) {
        setCreditCards([])
      }
    } finally {
      if (isMountedRef.current) {
        setLoadingCards(false)
      }
    }
  }

  useEffect(() => {
    if (isGoingToFree || isPixPayment || (planPreview && !needsCreditCard)) {
      setLoadingCards(false)
      return
    }

    if (!planPreview) return

    fetchCreditCards()
    fetchCustomerData()
  }, [isGoingToFree, isPixPayment, needsCreditCard, planPreview])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const applyPendingSubscription = useCallback((subscription) => {
    if (!subscription) return false
    notifySubscriptionUpdated(subscription)

    if (subscription.pending_payment) {
      setPendingSubscription(subscription)
      return true
    }

    setPendingSubscription(null)
    if (subscription.plan?.id === planId) {
      setPaymentStatus("success")
      timeoutRef.current = window.setTimeout(() => {
        if (isMountedRef.current) window.location.assign("/dashboard")
      }, 3000)
    } else {
      navigate("/dashboard/workspace/plan", {
        replace: true,
        state: {
          subscription,
          notice: "A compra PIX não está mais pendente. Confira abaixo a situação atual do plano.",
        },
      })
    }
    return false
  }, [navigate, planId])

  const refreshPendingSubscription = useCallback(async () => {
    try {
      const response = await Api("subscription/view")()
      if (!isMountedRef.current || response?.status !== 200) return false
      return applyPendingSubscription(response.data?.data)
    } catch (error) {
      console.error("Erro ao verificar pagamento PIX:", error)
      return true
    }
  }, [applyPendingSubscription])

  useEffect(() => {
    if (!pendingSubscription?.pending_payment?.id) return undefined

    let active = true
    let pollTimeout

    const poll = async () => {
      const shouldContinue = await refreshPendingSubscription()
      if (active && shouldContinue) {
        pollTimeout = window.setTimeout(poll, 10000)
      }
    }

    pollTimeout = window.setTimeout(poll, 10000)
    return () => {
      active = false
      window.clearTimeout(pollTimeout)
    }
  }, [pendingSubscription?.pending_payment?.id, refreshPendingSubscription])

  const handleValidateCoupon = async (couponCode) => {
    try {
      const normalizedCoupon = normalizeCouponCode(couponCode)
      const validateResponse = await Api("coupon/validate")(normalizedCoupon)

      if (validateResponse?.status !== 200) {
        return {
          success: false,
          message: validateResponse?.data?.message || "Cupom inválido",
        }
      }

      const couponData = validateResponse?.data?.data || {}

      return {
        success: true,
        code: normalizedCoupon,
        coupon: couponData,
      }
    } catch (error) {
      return {
        success: false,
        message: error?.response?.data?.message || "Não foi possível validar o cupom",
      }
    }
  }

  const handlePaymentAndPlanChange = async () => {
    if (isProcessing || submitInFlightRef.current) return

    setErrorMessage(null)

    let freshPreview
    try {
      submitInFlightRef.current = true
      setIsProcessing(true)
      freshPreview = await fetchPlanPreview(planId, false)
    } catch (_) {
      submitInFlightRef.current = false
      setIsProcessing(false)
      return
    }

    const freshNeedsPaymentMethod = needsCreditCardForPlanChange(
      freshPreview,
      currentBillingInterval,
      planBillingInterval,
      !isGoingToFree
    )
    if (shouldCreatePixInvoice(freshPreview)) {
      try {
        const profileStatus = await getBillingProfileStatus(freshPreview)
        if (shouldRedirectToBillingProfile(profileStatus)) {
          submitInFlightRef.current = false
          setIsProcessing(false)
          navigate("/dashboard/workspace/billing/account-identification", {
            replace: true,
            state: {
              returnTo: location.pathname,
              returnState: buildPixPurchaseResumeState(location.state),
            },
          })
          return
        }
      } catch (error) {
        console.error("Erro ao verificar dados de cobrança:", error)
        setErrorMessage(
          error?.response?.data?.message || error?.message || "Não foi possível verificar os dados de cobrança.",
        )
        submitInFlightRef.current = false
        setIsProcessing(false)
        return
      }
    }

    if (freshNeedsPaymentMethod && !paymentData.isNewCard && !paymentData.selectedCardId) {
      setErrorMessage("Selecione um cartão ou adicione um novo para continuar.")
      submitInFlightRef.current = false
      setIsProcessing(false)
      return
    }

    if (freshNeedsPaymentMethod && paymentData.isNewCard && !paymentData.isNewCardValid) {
      setForceValidation(true)
      submitInFlightRef.current = false
      setIsProcessing(false)
      return
    }

    try {
      setForceValidation(false)
      setPaymentStatus("processing")

      if (freshNeedsPaymentMethod && paymentData.isNewCard && paymentData.newCardData) {
        const cardData = paymentData.newCardData

        if (needsCustomerUpdate) {
          await updateCustomerData(cardData)
        }
      }

      const subscriptionPayload = {
        plan_id: planId,
      }

      if (freshPreview.billing_type) {
        subscriptionPayload.billing_type = freshPreview.billing_type
      }

      if (freshPreview.billing_type !== "Pix" && freshPreview.direction === "Upgrade" && appliedCoupon?.code) {
        subscriptionPayload.coupon_code = appliedCoupon.code
      }

      if (freshNeedsPaymentMethod && !paymentData.isNewCard && paymentData.selectedCardId) {
        subscriptionPayload.credit_card_id = paymentData.selectedCardId
      } else if (freshNeedsPaymentMethod && paymentData.isNewCard && paymentData.newCardData) {
        const cardData = paymentData.newCardData
        subscriptionPayload.credit_card = {
          number: cardData.number.replace(/\s/g, ""),
          name_on_card: cardData.name_on_card,
          expiration_month: Number.parseInt(cardData.expiration_month),
          expiration_year: Number.parseInt(cardData.expiration_year),
          security_code: Number.parseInt(cardData.security_code),
          holder: {
            name: cardData.holder_name,
            email: cardData.holder_email,
            taxpayer_id: cardData.holder_taxpayer_id.replace(/\D/g, ""),
            postal_code: cardData.postal_code.replace(/\D/g, ""),
            address_line1: cardData.address_line1,
            address_line2: cardData.address_line2 || "",
            house_number: cardData.house_number,
            phone_number: cardData.holder_phone.replace(/\D/g, ""),
            neighborhood: cardData.neighborhood,
            city: cardData.city,
            state: cardData.state,
          },
        }
      }

      const response = await Api("subscription/update")(subscriptionPayload)

      if (!isMountedRef.current) return

      if (response?.status === 200) {
        const updatedSubscription = response?.data?.data
        notifySubscriptionUpdated(updatedSubscription)
        if (updatedSubscription?.pending_payment) {
          setPendingSubscription(updatedSubscription)
          setPaymentStatus(null)
          return
        }

        setPaymentStatus("success")
        timeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            window.location.assign("/dashboard")
          }
        }, 3000)
      } else {
        throw new Error(response?.data?.message || "Erro ao alterar plano")
      }
    } catch (error) {
      console.error("Erro no pagamento:", error)
      if (isMountedRef.current) {
        const requestErrorMessage = getPaymentErrorMessage(error, "Erro ao alterar plano")
        setErrorMessage(requestErrorMessage)
        const status = error?.response?.status
        if (status === 409) {
          try {
            const subscriptionResponse = await Api("subscription/view")()
            const subscription = subscriptionResponse?.data?.data
            if (isMountedRef.current && subscription) {
              if (subscription.pending_payment) {
                notifySubscriptionUpdated(subscription)
                setPendingSubscription(subscription)
                setPaymentStatus(null)
                return
              }
              navigate("/dashboard/workspace/plan", {
                replace: true,
                state: {
                  subscription,
                  notice: subscription.pending_payment
                    ? "A solicitação já foi recebida. Confira o PIX pendente abaixo."
                    : "A assinatura foi atualizada. Confira os dados atuais do plano.",
                },
              })
              return
            }
          } catch (_) {}
        }
        setPaymentStatus(
          freshPreview?.billing_type === "Pix" ||
          freshPreview?.direction === "Downgrade" ||
          [400, 403, 404, 409].includes(status)
            ? null
            : "failure"
        )
      }
    } finally {
      submitInFlightRef.current = false
      if (isMountedRef.current) {
        setIsProcessing(false)
      }
    }
  }

  useEffect(() => {
    if (
      !location.state?.resumePixPurchase ||
      resumePixPurchaseRef.current ||
      loadingPreview ||
      !planPreview ||
      previewError
    ) {
      return
    }

    resumePixPurchaseRef.current = true
    const { resumePixPurchase, ...checkoutState } = location.state
    navigate(location.pathname, { replace: true, state: checkoutState })
    handlePaymentAndPlanChange()
  }, [loadingPreview, location.pathname, location.state, navigate, planPreview, previewError])

  const handleReturnFromProcessing = () => {
    if (paymentStatus === "success") {
      navigate("/dashboard/workspace")
    } else if (paymentStatus === "failure") {
      setPaymentStatus(null)
      setForceValidation(false)
    }
  }

  const Total = planPreview?.charge_today ?? (typeof planPrice === "number" ? planPrice : 0)

  if (!selectedPlan?.id) {
    return null
  }

  if (pendingSubscription?.pending_payment) {
    return (
      <Dashboard.Content style={{ maxWidth: "688px" }}>
        <Dashboard.Header
          returnButton={true}
          returnHref="/dashboard/workspace/plan"
          title="Pagamento via PIX"
          className="d-flex reverse-row"
        />
        <PixPendingPayment
          payment={pendingSubscription.pending_payment}
          planName={planDisplayName}
          onUnavailable={refreshPendingSubscription}
          secondaryLabel="Pagar depois"
          onSecondary={() => navigate("/dashboard/workspace/plan", {
            replace: true,
            state: { subscription: pendingSubscription },
          })}
        />
        <p className="plan-change-pix-note" aria-live="polite">
          <Spinner size={18} /> Aguardando a confirmação do pagamento. Esta tela será atualizada automaticamente.
        </p>
      </Dashboard.Content>
    )
  }

  if (paymentStatus) {
    return <PaymentProcessing state={paymentStatus} data={{ total: Total, name: userName, direction: planPreview?.direction, errorMessage }} onReturn={handleReturnFromProcessing} />
  }

  return (
    <Dashboard.Content style={{ maxWidth: "688px" }}>
      <Dashboard.Header
        returnButton={true}
        returnHref={returnHref}
        title="Contratação de plano"
        className="d-flex reverse-row"
      />

      {errorMessage && (
        <div ref={errorAlertRef} tabIndex="-1">
          <Alert variant="danger" onClose={() => setErrorMessage(null)} role="alert">
            {errorMessage}
          </Alert>
        </div>
      )}

      <Card className="plan-change-card">
        <div className="plan-change-hero">
          <div>
            <span className="plan-change-kicker">
              {planBillingInterval === "Yearly" ? "Plano anual" : "Plano mensal"}
            </span>
            <h2>{planDisplayName}</h2>
            <p><Icon id="icon-file" size="18" /> {planMaxDocuments} documentos renovados todo mês</p>
          </div>
        </div>

        {loadingPreview && (
          <div className="plan-change-loading" aria-live="polite">
            <span className="spinner-border spinner-border-sm" aria-hidden="true" />
            Calculando sua melhor condição...
          </div>
        )}
        {previewError && <Alert variant="danger">{previewError}</Alert>}

        {planPreview && (
          <div className="plan-change-quote">
            <div className={`plan-change-status ${isDowngrade || willCreatePixInvoice ? "scheduled" : "immediate"}`}>
              <Icon id={isDowngrade || willCreatePixInvoice ? "icon-calendar_month" : "icon-check_circle"} size="22" />
              <div>
                <strong>{isDowngrade ? "Mudança agendada" : willCreatePixInvoice ? "Pagamento por PIX" : "Novo plano liberado imediatamente"}</strong>
                <span>
                  {isDowngrade
                    ? `Seu plano atual continua até ${formatCalendarDate(planPreview.term_start_date)}.`
                    : willCreatePixInvoice
                      ? "Seu plano atual continua ativo e o novo plano será liberado após a confirmação do pagamento."
                    : `Você recebe ${planPreview.documents_granted} documentos assim que confirmar.`}
                </span>
              </div>
            </div>

            <div className="plan-change-values">
              <div><span>Valor do novo plano</span><strong>{formatBRL(planPreview.target_plan.price)}</strong></div>
              {!isDowngrade && planPreview.credit > 0 && (
                <div className="credit"><span>Crédito do plano atual</span><strong>− {formatBRL(planPreview.credit)}</strong></div>
              )}
              <div className="total">
                <span>{isDowngrade ? "Cobrança hoje" : "Você paga hoje"}</span>
                <strong>{formatBRL(planPreview.charge_today)}</strong>
              </div>
            </div>

            {!isDowngrade && planPreview.documents_forfeited > 0 && planPreview.credit > 0 && (
              <div className="plan-change-impact">
                <Icon id="icon-cached" size="22" />
                <p>
                  Seus <strong>{planPreview.documents_forfeited} documentos restantes</strong> viram crédito e serão
                  substituídos por <strong>{planPreview.documents_granted} documentos/mês</strong>.
                </p>
              </div>
            )}

            <div className="plan-change-date">
              <Icon id={willCreatePixInvoice ? "icon-qr_code_scanner" : "icon-calendar_today"} size="20" />
              <div>
                <span>{isDowngrade ? "Data da alteração" : willCreatePixInvoice ? "Forma de pagamento" : "Próxima cobrança"}</span>
                <strong>
                  {isDowngrade
                    ? formatCalendarDate(planPreview.term_start_date)
                    : willCreatePixInvoice
                      ? "PIX — vencimento informado após gerar"
                      : formatCalendarDate(planPreview.next_due_date)}
                </strong>
              </div>
            </div>

            {planPreview.charge_waived && (
              <div className="plan-change-waived"><Icon id="icon-check" size="18" /> A diferença ficou abaixo do mínimo e não será cobrada.</div>
            )}

            {!isDowngrade && !isPixPayment && (
              <div className="plan-change-coupon">
                <CouponAppliedSection
                  coupon={appliedCoupon}
                  code={appliedCoupon?.code}
                  context="plan"
                  planMaxDocuments={planMaxDocuments}
                  planName={planDisplayName}
                  onRemove={() => setAppliedCoupon(null)}
                  onChange={() => setShowCupomModal(true)}
                />
              </div>
            )}
          </div>
        )}
      </Card>
      {needsCreditCard && (
        <section className="plan-change-payment">
          <h3>
            {loadingCards
                ? "Carregando..."
              : creditCards.length > 0
                ? "Cartão para cobrança"
                : "Insira os dados do cartão"}
          </h3>
          <div className="card-select pb-3">
            {loadingCards ? (
              <div className="text-center p-4">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Carregando...</span>
                </div>
              </div>
            ) : creditCards.length > 0 ? (
              <CardSelect creditCards={creditCards} onCardSelect={setPaymentData} forceValidation={forceValidation} />
            ) : (
              <div className="card-select-item">
                <CreditForm onFormChange={handleFormChange} forceValidation={forceValidation} />
              </div>
            )}
          </div>
        </section>
      )}
        <div className="plan-change-actions">
          <Button variant="border" onClick={() => navigate(returnHref)} disabled={isProcessing}>
            Voltar
          </Button>
          <Button
            variant="primary"
            className="text-uppercase btn-responsive"
            onClick={handlePaymentAndPlanChange}
            disabled={
              isProcessing ||
              loadingPreview ||
              !planPreview ||
              !!previewError ||
              (needsCreditCard && !paymentData.isNewCard && !paymentData.selectedCardId) ||
              (needsCreditCard && paymentData.isNewCard && !paymentData.isNewCardValid)
            }
            title={
              needsCreditCard && paymentData.isNewCard && !paymentData.isNewCardValid
                ? "Corrija os erros do formulário do cartão para continuar"
                : undefined
            }
          >
            {isProcessing
              ? "Processando..."
              : isDowngrade
                ? "Agendar alteração"
                : willCreatePixInvoice
                  ? "Gerar PIX"
                : planPreview?.charge_today === 0
                  ? "Confirmar alteração"
                  : "Pagar e continuar"}
          </Button>
        </div>
      <CupomModal
        show={showCupomModal}
        onClose={() => setShowCupomModal(false)}
        cupom={appliedCoupon?.code}
        context="plan"
        onValidateCoupon={handleValidateCoupon}
        onConfirmCoupon={setAppliedCoupon}
      />

      <ResponsiveAlert
        show={showAlert}
        onClose={() => setShowAlert(false)}
        text="Plano alterado com sucesso!"
      />

    </Dashboard.Content>
  )
}

export default ChangePlan
